import * as Sentry from '@sentry/react'

const isDevelopment = process.env.BUILD_ENV === 'development'
const isDebugSentry = process.env.DEBUG_SENTRY === 'true'
const localSentryConsoleHosts = [
  /^localhost$/i,
  /^127\./,
  /^::1$/,
  /\.test$/i,
]

const maxContextDepth = 10
const shouldEnableSentry = Boolean(process.env.SENTRY_DSN)
const isLocalSentryConsoleHost = () => {
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname

  return localSentryConsoleHosts.some(pattern => pattern.test(hostname))
}

// This path must match the Chef-managed Sentry tunnel location for each deployed Teams domain.
const sentryTunnel = isDevelopment || isLocalSentryConsoleHost() ? undefined : '/sentry/tunnel'

const configuredIgnoreErrors = [
  // Random plugins/extensions and injected browser scripts.
  'top.GLOBALS',
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'Can\'t find variable: DarkReader',
  'Can\'t find variable: __firefox__',
  'Cannot redefine property: BetterJsPop',
  'Could not establish connection. Receiving end does not exist.',
  'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
  'Invalid call to runtime.sendMessage(). Tab not found.',
  'Non-Error promise rejection captured with value: Request timeout',
  'Object captured as promise rejection with keys: code, message',
  'Object captured as promise rejection with keys: code, message, stack',
  'chrome-extension',
  /undefined is not an object \(evaluating 'window\.__firefox__\..+'\)/i,

  // Browser/runtime noise that is not actionable for this app.
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  /Should not already be working\.?/i,

  // Cancelled or interrupted network/loading work.
  'AbortError: Fetch is aborted',
  'AbortError: The operation was aborted.',
  'AbortError: The user aborted a request.',
  'ChunkLoadError',
  'Network request failed',
  'Non-Error promise rejection captured with keys',
  'Non-Error promise rejection captured with value: abort',
  'Request was aborted',
  'TypeError: Cancelled',
  'TypeError: Failed to fetch',
  'TypeError: Load failed',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: cancelled',
  'TypeError: network error',
  'Worker was terminated',
]

const configuredDenyUrls = [
  // Browser extensions.
  /extensions\//i,
  /^chrome:\/\//i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,

  // Common third-party script noise.
  /connect\.facebook\.net\/en_US\/all\.js/i,
  /graph\.facebook\.com/i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  /pagead\/js/i,
  /webappstoolbarba\.texthelp\.com\//i,
]

const getCauseMessages = originalException => {
  const messages = []
  let currentError = originalException
  let iterations = 0

  while (currentError && iterations < 5) {
    const message = typeof currentError === 'string'
      ? currentError
      : currentError?.message

    if (typeof message === 'string' && message) {
      messages.push(message)
    }

    const text = currentError?.toString?.()

    if (typeof text === 'string' && text) {
      messages.push(text)
    }

    currentError = currentError?.cause
    iterations += 1
  }

  return messages
}

const getPotentialErrorMessages = ({ event, hint }) => {
  const messages = [
    event?.message,
    event?.logentry?.message,
    event?.logentry?.formatted,
    ...getCauseMessages(hint?.originalException),
  ].filter(Boolean)

  const exceptionValues = event?.exception?.values || []

  exceptionValues.forEach(({ type, value }) => {
    if (!value) {
      return
    }

    messages.push(value)

    if (type) {
      messages.push(`${type}: ${value}`)
    }
  })

  return [...new Set(messages)]
}

const matchesIgnorePattern = ({ message, pattern }) => {
  if (typeof message !== 'string' || !message || !pattern) {
    return false
  }

  if (pattern instanceof RegExp) {
    return RegExp(pattern.source, pattern.flags).test(message)
  }

  if (typeof pattern === 'string') {
    return message.includes(pattern)
  }

  return false
}

const hasIgnoredMessage = ({ messages, patterns }) => (
  messages.some(message => patterns.some(pattern => matchesIgnorePattern({ message, pattern })))
)

export const shouldIgnoreSentryEvent = (event, hint) => (
  hasIgnoredMessage({
    messages: getPotentialErrorMessages({ event, hint }),
    patterns: configuredIgnoreErrors,
  })
)

const getConsoleNote = shouldSendToSentry => {
  if (shouldSendToSentry) {
    return [
      'DEBUG_SENTRY=true is enabled, so this local development event will be sent to Sentry.',
      'Use this only when intentionally QAing Sentry transport.',
    ].join('\n')
  }

  return [
    'In local development, this event was logged here but was not sent to Sentry.',
    'In staging and production, this event would be sent to Sentry unless filtered.',
    'If this represents an unexpected error, fix it before shipping.',
    '',
    'To test Sentry transport locally, restart with DEBUG_SENTRY=true.',
  ].join('\n')
}

function logEventToConsole(event, hint, { shouldSendToSentry }) {
  const {
    contexts,
    event_id: eventId,
    level,
    message,
    tags,
    user,
    ...other
  } = event

  const { originalException } = hint || {}

  /* eslint-disable no-console */
  console.group('%cSentry Event', 'color: red;')
  console.info(originalException?.toString?.() || message)

  console.groupCollapsed('Details')

  console.group('Event ID')
  console.info(eventId)
  console.groupEnd()

  console.group('Contexts')
  console.info(contexts)
  console.groupEnd()

  console.group('Level')
  console.info(level)
  console.groupEnd()

  console.group('Tags')
  console.info(tags)
  console.groupEnd()

  console.group('User')
  console.info(user)
  console.groupEnd()

  console.group('Other')
  console.info(other)
  console.groupEnd()

  console.group('Note')
  console.info(getConsoleNote(shouldSendToSentry))
  console.groupEnd()

  console.group('Stack')
  console.trace(originalException?.stack)
  console.groupEnd()

  console.groupEnd()
  console.groupEnd()
  /* eslint-enable no-console */
}

if (shouldEnableSentry) {
  Sentry.init({
    beforeSend(event, hint) {
      // Filter here instead of ignoreErrors so nested Error.cause messages are considered.
      if (shouldIgnoreSentryEvent(event, hint)) {
        return null
      }

      const shouldLogEventToConsole = isDevelopment || isLocalSentryConsoleHost()
      const shouldSendToSentry = isDebugSentry || !shouldLogEventToConsole

      if (shouldLogEventToConsole) {
        logEventToConsole(event, hint, { shouldSendToSentry })
      }

      if (!shouldSendToSentry) {
        return null
      }

      return event
    },
    denyUrls: configuredDenyUrls,
    dsn: process.env.SENTRY_DSN,
    environment: process.env.BUILD_ENV || process.env.NODE_ENV,
    integrations: defaultIntegrations => [
      ...defaultIntegrations.filter(integration => integration.name !== 'BrowserSession'),
      Sentry.extraErrorDataIntegration({
        depth: maxContextDepth,
      }),
      Sentry.breadcrumbsIntegration({
        console: false,
      }),
    ],
    normalizeDepth: maxContextDepth,
    release: process.env.SENTRY_RELEASE,
    transportOptions: {
      fetchOptions: {
        credentials: 'omit',
      },
    },
    tunnel: sentryTunnel,
  })
}
